// frontend/src/pages/Referral.jsx
import { useState, useEffect, useCallback } from 'react';
import { authAPI, walletAPI } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import {
  Card, Button, Badge, StatCard, Spinner, Empty, Table, Td, SectionHeader,
} from '../components/ui';
import toast from 'react-hot-toast';
import { Copy, RefreshCw, Share2, ArrowRightLeft } from 'lucide-react';
import { format } from 'date-fns';

const fmtUGX = (n) => 'UGX ' + Math.round(n || 0).toLocaleString();

export default function Referral() {
  const { user } = useAuth();
  const [referral,    setReferral]    = useState(null);
  const [commissions, setCommissions] = useState([]);
  const [wallet,      setWallet]      = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [xferLoading, setXferLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [refRes, txRes, walRes] = await Promise.allSettled([
        authAPI.getReferralData(),
        walletAPI.getTransactions({ type: 'referral_commission', limit: 50 }),
        walletAPI.getBalance(),
      ]);
      if (refRes.status === 'fulfilled') setReferral(refRes.value);
      if (txRes.status === 'fulfilled')  setCommissions(txRes.value.transactions || []);
      if (walRes.status === 'fulfilled') setWallet(walRes.value.wallet || null);
    } catch {
      toast.error('Failed to load referral data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const referralCode = referral?.referralCode || user?.referral?.code || '—';
  const referralLink = referral?.referralLink ||
    (referralCode && referralCode !== '—'
      ? 'https://dwifintech.com/register?ref=' + referralCode
      : '');

  const copy = (text) => navigator.clipboard.writeText(text).then(() => toast.success('Copied!'));

  const share = () => {
    const msg = 'Earn 5% of every internet package your invitees buy — forever. Join DWI Fintech: ' + referralLink;
    if (navigator.share) {
      navigator.share({ title: 'Earn with DWI Fintech', text: msg, url: referralLink });
    } else {
      copy(referralLink);
    }
  };

  const shareWhatsApp = () => {
    const msg = 'Earn 5% of every internet package your invitees buy — forever!\nJoin DWI FINTECH: ' + referralLink;
    window.open('https://wa.me/?text=' + encodeURIComponent(msg), '_blank');
  };

  // Transfer commission balance → primary UGX wallet
  const handleTransferCommission = async () => {
    const commBal = Math.floor(wallet?.commissionBalance || 0);
    if (commBal <= 0) { toast.error('No commission balance to transfer'); return; }
    setXferLoading(true);
    try {
      const res = await walletAPI.commissionToPrimary({ amountUGX: commBal });
      toast.success(res.message || `${fmtUGX(commBal)} moved to your UGX wallet`);
      fetchData();
    } catch (err) {
      toast.error(err.message || 'Transfer failed — please try again');
    } finally {
      setXferLoading(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Spinner size="xl" /></div>;
  }

  const r        = referral || {};
  const commBal  = wallet?.commissionBalance || 0;
  const totalEarned = r.totalEarned || 0;

  return (
    <div className="space-y-6 animate-slide-up">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-black text-text-primary tracking-tight">Referral & Reseller</h1>
          <p className="text-text-secondary text-sm mt-1">Earn 5% of every internet package your invitees buy — forever.</p>
        </div>
        <button onClick={fetchData} className="text-text-muted hover:text-cyan transition-colors p-2 rounded-xl hover:bg-cyan/10">
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard label="Total Earned"       value={fmtUGX(totalEarned)}  sub="All-time commissions"   color="#EAB308" icon="💰" />
        <StatCard label="Commission Balance" value={fmtUGX(commBal)}      sub="Available to transfer"  color="#10B981" icon="💳" />
        <StatCard label="Level 1 Referrals"  value={r.level1Count || 0}   sub="Direct invites"         color="#00A8DB" icon="👥" />
        <StatCard label="Total Network"      value={(r.level1Count || 0) + (r.level2Count || 0) + (r.level3Count || 0)} sub="All 3 levels" color="#8B5CF6" icon="🔗" />
      </div>

      {/* Commission wallet + transfer */}
      {commBal > 0 && (
        <Card className="border-emerald-500/30 bg-emerald-500/5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-text-muted text-xs font-bold uppercase tracking-wider mb-1">Commission Balance</p>
              <p className="text-2xl font-black text-emerald-400">{fmtUGX(commBal)}</p>
              <p className="text-text-secondary text-xs mt-1">Ready to transfer to your UGX wallet</p>
            </div>
            <Button
              onClick={handleTransferCommission}
              disabled={xferLoading}
              className="flex items-center gap-2"
              style={{ background: '#10B981', borderColor: '#10B981' }}
            >
              <ArrowRightLeft className="w-4 h-4" />
              {xferLoading ? 'Transferring…' : 'Transfer to Wallet'}
            </Button>
          </div>
        </Card>
      )}

      {/* Referral link card */}
      <Card>
        <SectionHeader title="Your Referral Code & Link" />
        <div className="space-y-3">
          <div>
            <p className="text-text-muted text-xs font-bold uppercase tracking-wider mb-2">Referral Code</p>
            <div className="flex items-center gap-3 bg-bg border border-border rounded-xl px-4 py-3">
              <code className="text-cyan font-black text-2xl tracking-widest flex-1">{referralCode}</code>
              <button onClick={() => copy(referralCode)} className="text-text-muted hover:text-cyan transition-colors">
                <Copy className="w-5 h-5" />
              </button>
            </div>
          </div>
          <div>
            <p className="text-text-muted text-xs font-bold uppercase tracking-wider mb-2">Referral Link</p>
            <div className="flex items-center gap-2 bg-bg border border-border rounded-xl px-4 py-3">
              <code className="text-text-secondary text-xs flex-1 truncate">{referralLink || '—'}</code>
              <button onClick={() => copy(referralLink)} className="text-text-muted hover:text-cyan transition-colors flex-shrink-0">
                <Copy className="w-4 h-4" />
              </button>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button className="flex-1 min-w-[140px]" onClick={() => copy(referralLink)}>
              <Copy className="w-4 h-4" /> Copy Link
            </Button>
            <Button variant="outline" onClick={share}>
              <Share2 className="w-4 h-4" /> Share
            </Button>
            <Button variant="outline" onClick={shareWhatsApp}
              style={{ background: '#25D36620', borderColor: '#25D366', color: '#25D366' }}>
              WhatsApp
            </Button>
          </div>
        </div>
      </Card>

      {/* Commission structure */}
      <Card>
        <SectionHeader title="Commission Structure" />
        <div className="grid md:grid-cols-3 gap-3">
          {[
            { level: 'Level 1', isp: '5%', exchange: '0.3%', desc: 'Direct referrals you invite',   count: r.level1Count || 0, color: '#00A8DB' },
            { level: 'Level 2', isp: '1%', exchange: '0.15%', desc: "People your referrals invite", count: r.level2Count || 0, color: '#8B5CF6' },
            { level: 'Level 3', isp: '0.5%', exchange: '0.05%', desc: 'Third-generation network',   count: r.level3Count || 0, color: '#10B981' },
          ].map((l) => (
            <div key={l.level} className="rounded-xl p-4 bg-bg-panel border border-border" style={{ borderColor: `${l.color}30` }}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-text-muted text-xs font-bold uppercase tracking-wider">{l.level}</span>
                <span className="font-black text-xl" style={{ color: l.color }}>{l.isp}</span>
              </div>
              <p className="text-text-secondary text-xs mb-1">{l.desc}</p>
              <p className="text-text-muted text-[10px] mb-3">ISP {l.isp} · Exchange {l.exchange}</p>
              <p className="text-text-primary font-bold text-sm">{l.count} referrals</p>
            </div>
          ))}
        </div>
        <div className="mt-4 bg-cyan/5 border border-cyan/20 rounded-xl p-4">
          <p className="text-cyan font-bold text-xs uppercase tracking-wider mb-2">How It Works</p>
          <ul className="space-y-1.5 text-text-secondary text-xs">
            <li>• Earn <strong className="text-text-primary">5%</strong> of every internet package your Level 1 referrals buy — forever</li>
            <li>• Their invitees (Level 2) earn you <strong className="text-text-primary">1%</strong>; Level 3 earns <strong className="text-text-primary">0.5%</strong></li>
            <li>• Exchange orders also earn commissions (0.3% / 0.15% / 0.05%)</li>
            <li>• Commissions land instantly in your Commission Wallet — transfer to UGX anytime</li>
          </ul>
        </div>
      </Card>

      {/* Referred users */}
      <Card>
        <SectionHeader title="Your Referred Members" />
        {!r.referredUsers || r.referredUsers.length === 0 ? (
          <Empty
            icon="👥"
            title="No referrals yet"
            description="Share your referral link to start earning commissions."
            action={<Button onClick={share}><Share2 className="w-4 h-4" /> Share Link</Button>}
          />
        ) : (
          <Table headers={['Name', 'Email', 'Joined', 'Status']}>
            {r.referredUsers.map((u) => (
              <tr key={u._id}>
                <Td className="font-semibold text-text-primary">{u.name}</Td>
                <Td className="text-xs">{u.email}</Td>
                <Td className="text-xs text-text-muted">
                  {u.createdAt ? format(new Date(u.createdAt), 'dd MMM yyyy') : '—'}
                </Td>
                <Td><Badge label="Active" color="green" /></Td>
              </tr>
            ))}
          </Table>
        )}
      </Card>

      {/* Commission history */}
      <Card>
        <SectionHeader title="Commission History" />
        {commissions.length === 0 ? (
          <Empty icon="💰" title="No commissions yet" description="Commissions appear here after your referrals buy internet packages or exchange currency." />
        ) : (
          <Table headers={['Amount', 'Event', 'Level', 'Date']}>
            {commissions.map((tx) => (
              <tr key={tx._id}>
                <Td className="font-bold text-emerald-400">+{fmtUGX(tx.amount)}</Td>
                <Td className="text-xs">{tx.metadata?.eventType || tx.description || 'Referral commission'}</Td>
                <Td className="text-xs text-text-muted">
                  {tx.metadata?.level ? `Level ${tx.metadata.level} (${tx.metadata.ratePct}%)` : '—'}
                </Td>
                <Td className="text-xs text-text-muted">
                  {tx.createdAt ? format(new Date(tx.createdAt), 'dd MMM yyyy HH:mm') : '—'}
                </Td>
              </tr>
            ))}
          </Table>
        )}
      </Card>
    </div>
  );
}
